import React, { useEffect, useRef } from "react";
import { checkCurrentJwtExp, getXToken } from "lib/http/unAuthSetup";
import nookies, { parseCookies, setCookie } from "nookies";
import { GetServerSidePropsContext } from "next";
import { useRouter } from "next/router";
import NewsListWrapper from "core/feed/UI/NewsListWrapper";
import { useFeedValues } from "lib/context/FeedContext";
import Head from "next/head";
import { track } from "lib/analytics/analytics";
import {
  AnalyticsEvents,
  InternalAnalyticsEvents,
} from "lib/analytics/constants";
import { useTracking } from "core/tracking/useTracking";
import { handleTokenExchange } from "core/account/handleTokenExchange";

interface GetServerSideProps {
  xToken: string;
  initialQuery: string | null;
  hasEditedSearch: boolean;
  initialData: any[];
  initialHeader: number | null;
}

interface JobCookies {
  xToken?: string;
  userToken?: string;
  userRefId?: string;
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const cookies = parseCookies(ctx) as JobCookies;
  const xToken = getXToken(cookies.xToken);
  const { query, code, destination, userrefid, token } = ctx.query;
  // const preloadedNews = await newsFetcher(
  //   "v1/web/maritime/news?origin=Web&limit=25"
  // );
  const existingToken = cookies.userToken;
  const existingRefId = cookies.userRefId;

  if (code && Boolean(!existingToken || !existingRefId)) {
    const result = await handleTokenExchange(
      code as string,
      destination ? (destination as string) : "web"
    );
    if (result) {
      // Set cookied here instead of putting in destination path
      nookies.set(ctx, "userToken", result.token, {
        maxAge: 30 * 24 * 60 * 60,
        path: "/",
      }); // 30 days validity for example
      nookies.set(ctx, "userRefId", result.userRefId, {
        maxAge: 30 * 24 * 60 * 60,
        path: "/",
      });

      return {
        redirect: {
          destination: `/news?userrefid=${result.userRefId}&token=true`,
        },
      };
    } else {
      return {
        redirect: {
          destination: "/news?exchangeerror=true",
        },
      };
    }
  } else if (code && Boolean(existingToken && existingRefId)) {
    return {
      redirect: {
        destination: `/news?userrefid=${existingRefId}&token=true`, //&token=${existingToken}`,
      },
    };
  }
  return {
    props: {
      // initialData: preloadedNews?.data
      //   ? (preloadedNews?.data as NewsFeedArticle[])
      //   : null,
      // initialHeader: preloadedNews?.headers?.num_results ?? null,
      initialData: null,
      initialHeader: null,
      initialQuery: query ?? null,
      xToken,
      ...(existingToken &&
        existingRefId && {
        token: existingToken,
        userRefId: existingRefId,
      }),
      ...(token &&
        userrefid && {
        token: token ?? null,
        userRefId: userrefid ?? null,
      }),
    },
  };
}

export default function News(props: GetServerSideProps) {
  const router = useRouter();

  const { xToken, initialQuery, initialData, initialHeader } = props;
  const initialLoad = useRef<boolean>(true);
  const { getFeedValues, setFeedValues } = useFeedValues();
  const { query } = getFeedValues();
  const { internalTrackEvent } = useTracking();

  const refreshData = () => {
    router.replace(router.asPath);
  };

  useEffect(() => {
    track(AnalyticsEvents.NEWS_FEED_VIEWED);
  }, []);
  const { xToken: currentX, hasEditedSearch } = parseCookies(null);
  const { validExpiry } = checkCurrentJwtExp(currentX);
  useEffect(() => {
    if (!router.isReady) return;
    if (!validExpiry) {
      refreshData();
    }
  }, [router.isReady, validExpiry]);

  useEffect(() => {
    const action = router.query["action"]
      ? router.query["action"].toString()
      : InternalAnalyticsEvents.WEBSITE_VISIT;
    internalTrackEvent(action, { path: "/news" });
  }, []);

  // only preload location on first load
  useEffect(() => {
    if (hasEditedSearch || query || !initialLoad.current) {
      return;
    }
    if (initialLoad.current && initialQuery) {
      setFeedValues({
        query: initialQuery ?? "",
      });
    }
  }, [initialQuery, setFeedValues, query, initialLoad, hasEditedSearch]);

  useEffect(() => {
    if (xToken && xToken !== currentX) {
      setCookie(null, "xToken", xToken, {});
    }
  }, [xToken, currentX]);
  // if expired, wait for it to generate new token
  return (
    <>
      <Head>
        <meta
          name="description"
          content="10ure News Feed. Stay up to date on all the latest events in the maritime industry."
        />
        {/* <meta
          name="keywords"
          content="10ure News Feed. Stay up to date on all the latest events in the maritime industry."
        /> */}
        <meta property="og:title" content="Maritime News - 10ure" />
        <meta
          property="og:description"
          content="Get informed on the latest maritime news!"
        />
        <meta
          property="og:image"
          content="https://firebasestorage.googleapis.com/v0/b/10ure/o/static_assets%2FHorizontal%20Banner.png?alt=media&token=707f9912-99dc-40b5-8fab-c0425675a53a"
        />
      </Head>
      <NewsListWrapper
        xToken={validExpiry ? currentX : undefined}
        initialData={initialData}
        initialHeader={initialHeader}
      />
    </>
  );
}
